// TextureSampler — per-pixel alpha mask for `pen='texture'`
// (texture-brush-design.md §1 / §2 / §3).
//
// The texture brush multiplies a grayscale **alpha mask** into the pen
// pattern at every stamped pixel (architecture.md §19.3 / §19.10 E). The
// mask is a plain integer alpha array `0..255`, NOT a decoded PNG — see §3:
//
//   server snapshot (`@napi-rs/canvas` / Skia) and the web client must be
//   **byte-identical**. A `PNG → Image/<canvas>` decode would diverge by a
//   bit or two between the browser decoder and Skia, breaking snapshot
//   parity. We therefore ship the mask as a `Uint8Array` baked into this
//   module so BOTH embedders sample the exact same bytes.
//
// Three mapping modes (texture-brush-design.md §1, 横展開 = 5 preset;
// `world-rotated` added phase11-light-pens-2026-08-21.md §2.5.2 4th real-
// device pass, for crayon streaks):
//
//   - `world` (紙 / カケアミ): `sample(tex, nx mod W, ny mod H)`. A pure
//     integer-coord function with no RNG, so client/server byte-identity is
//     self-evident. The texel grid is locked to canvas pixels → overlapping
//     strokes reveal a consistent下地 grain (= the essence of「紙」).
//
//   - `stamp` (毛先 / スプレー / 雲): the texel grid is locked to the *stamp*,
//     TRANSLATED to the stamp centre AND rotated by the stroke direction
//     angle θ, so毛筋 follow the筆運び. θ is derived from the segment
//     direction `(dx, dy)` via `dx/len`, `dy/len` (`len = sqrt(dx²+dy²)`) —
//     ONLY `sqrt` + `* + - /`, which are IEEE-754 correctly-rounded and
//     bit-identical across V8 platforms (the SAME determinism rule the AA
//     SDF path uses, modern-layout.md §9.9). `dot()` / zero-length segments
//     have no direction → θ=0 (cos=1, sin=0), still deterministic.
//
//     ⚠️ The per-dab TRANSLATION means a canvas pixel hit by many overlapping
//     dabs of one sweep (≈`penWidth` of them, ~1px apart) samples a
//     DIFFERENT texel from EACH dab (the texel grid re-centres every dab).
//     For a binary threshold seam this is fatal — accumulation is monotonic
//     ('add'), so "any one of ~penWidth independent samples inks" saturates
//     to solid fill almost everywhere (phase11-light-pens-2026-08-21.md
//     §2.5.2's "潰れ" failure, the stamp-mapping analogue of `airbrush-
//     design §2.1`'s dab-averaging). Fine for the alpha-multiply presets
//     (毛先/スプレー/雲 — no binary cutoff to collapse), NOT safe to reuse for
//     a new binary-threshold pen.
//
//   - `world-rotated` (currently: crayon's wax-grain map only): like `world`,
//     the texel grid is **canvas-anchored — NO per-dab translation** (so the
//     same canvas pixel always samples the same texel for a given direction,
//     regardless of how many overlapping dabs of the sweep hit it — the
//     `stamp` collapse above cannot happen). Like `stamp`, it **rotates by
//     the stroke direction** (canvas coords, not stamp-local, fed through the
//     SAME `-θ` rotation) so an anisotropic (direction-elongated) map reads
//     as streaks ALONG the stroke. Because the rotation angle is resolved
//     once per SEGMENT (`textureDir`, precomputed per `drawLine` call) and a
//     curving stroke's segments each carry a slightly different angle, a
//     texel run that reads as one continuous streak under one segment's
//     rotation naturally breaks up as later segments rotate the grid — which
//     is what makes the streaks stop being an unbroken "comb" the full length
//     of a curved stroke (phase11-light-pens-2026-08-21.md §2.5.2 4th pass).
//
// All three modes quantise the texel coordinate with `Math.floor` only (no
// transcendentals), so the texel lookup itself adds no float divergence on
// top of the cos/sin the caller already computed deterministically.
/** θ=0 — used by `dot()` and zero-length segments (no rotation). */
export const NO_DIRECTION = { cos: 1, sin: 0 };
/**
 * Derive the deterministic stroke-direction `{cos, sin}` from a segment
 * `(x1,y1)→(x2,y2)`. Uses ONLY `sqrt` + `* + - /` (IEEE-754
 * correctly-rounded, byte-identical across platforms — the SAME rule the AA
 * SDF coverage path follows). A zero-length segment returns `NO_DIRECTION`.
 */
export const directionFromSegment = (x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0)
        return NO_DIRECTION;
    return { cos: dx / len, sin: dy / len };
};
/**
 * Sample the texel alpha (0..255) for a canvas pixel `(nx, ny)` under the
 * sampler's `world` mapping: `alpha[(ny mod H) * W + (nx mod W)]`. Pure
 * integer-coord function → no RNG, no float rounding → byte-identical on
 * every platform (texture-brush-design.md §1 "world").
 *
 * The modulo uses non-negative wrap so negative canvas coordinates (which
 * `stampOnce` can iterate when a stamp straddles the top/left edge before
 * the bounds check) tile cleanly rather than reading out of range.
 *
 * Kept as a standalone export (not folded into `sampleTexel`) because the
 * world path is the hottest + simplest and several call sites + tests assert
 * it directly.
 */
export const sampleTextureAlpha = (tex, nx, ny) => {
    // Non-negative modulo (JS `%` keeps the sign of the dividend).
    const w = tex.width;
    const h = tex.height;
    let tx = nx % w;
    if (tx < 0)
        tx += w;
    let ty = ny % h;
    if (ty < 0)
        ty += h;
    return tex.alpha[ty * w + tx] ?? 0;
};
/**
 * Sample the texel alpha (0..255) for a canvas pixel `(nx, ny)` under EITHER
 * mapping mode. The kernel calls this once per stamped pixel.
 *
 *   - `world`: delegates to `sampleTextureAlpha` (cx/cy/dir ignored).
 *   - `stamp`: translates `(nx, ny)` into stamp-local coords relative to the
 *     stamp centre `(cx, cy)`, **rotates by -θ** (so the texture grid follows
 *     the stroke direction), divides by `scale`, and offsets to the texture
 *     centre. The resulting texel coords wrap (tiling) the same way `world`
 *     does. Rotation uses the precomputed `dir.{cos,sin}` (deterministic, see
 *     `directionFromSegment`) plus `Math.floor` — no transcendentals here.
 *   - `world-rotated`: like `stamp`, ROTATES by -θ, but does NOT translate to
 *     the stamp centre `(cx, cy)` (`cx`/`cy` are ignored, same as `world`) —
 *     see this file's module doc for why the translation is the one thing
 *     that must NOT carry over. Still divides by `scale` and floors + wraps
 *     the same way.
 *
 * Determinism: every operation is `+ - * /` + `Math.floor` over the already-
 * deterministic `cos`/`sin`, so the texel index is a pure function of the
 * inputs → web/server byte-identical (texture-brush-design.md §1 / §3).
 */
export const sampleTexel = (tex, nx, ny, cx, cy, dir) => {
    if (tex.mapping === 'world') {
        return sampleTextureAlpha(tex, nx, ny);
    }
    const scale = tex.scale && tex.scale > 0 ? tex.scale : 1;
    // `stamp` translates to the stamp centre first; `world-rotated` rotates the
    // raw world coords (pixel centres) with NO translation — the only
    // difference between the two branches (module doc above).
    const dxp = tex.mapping === 'stamp' ? nx + 0.5 - cx : nx + 0.5;
    const dyp = tex.mapping === 'stamp' ? ny + 0.5 - cy : ny + 0.5;
    // Rotate by -θ: [ cos  sin; -sin  cos ] (= inverse of the +θ rotation, so
    // the texture's local x-axis aligns with the stroke direction).
    const lx = dxp * dir.cos + dyp * dir.sin;
    const ly = -dxp * dir.sin + dyp * dir.cos;
    const w = tex.width;
    const h = tex.height;
    // Map local canvas units → texel units (÷scale) and offset to the texture
    // centre, then floor + non-negative wrap (tiling).
    let tx = Math.floor(lx / scale + w / 2) % w;
    if (tx < 0)
        tx += w;
    let ty = Math.floor(ly / scale + h / 2) % h;
    if (ty < 0)
        ty += h;
    return tex.alpha[ty * w + tx] ?? 0;
};
/**
 * `TextureMapping` → the numeric code the Wasm `register_texture` ABI
 * expects (`drawing-core-wasm/src/texture.rs`'s `MAPPING_WORLD` (0) /
 * `MAPPING_STAMP` (1) / `MAPPING_WORLD_ROTATED` (2)). Centralised here (was
 * `sampler.mapping === 'stamp' ? 1 : 0` inline at each `register_texture`
 * call site in `WasmStampRenderer.ts`) so adding `world-rotated` couldn't
 * silently fall through to the `world` code (0) at any call site — every
 * mapping value must be listed explicitly here or TypeScript's exhaustive
 * switch check on `TextureMapping` fails the build.
 */
export const textureMappingCode = (mapping) => {
    switch (mapping) {
        case 'world':
            return 0;
        case 'stamp':
            return 1;
        case 'world-rotated':
            return 2;
    }
};
//# sourceMappingURL=textureSampler.js.map